import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { loggedOut } from 'containers/auth/authSlice';
import { RootState } from 'store/reducers';
import { GlobalSearchResultItem, GlobalSearchTypeFilter } from './globalSearch.model';

interface GlobalSearchState {
  teamId: number | null;
  searchText: string;
  typeFilter: GlobalSearchTypeFilter;
  status: string;
  dateFrom: string;
  dateTo: string;
  results: GlobalSearchResultItem[];
  page: number;
  hasMore: boolean;
  scrollTop: number;
  lastQueryKey: string;
  keepState: boolean;
}

const initialState: GlobalSearchState = {
  teamId: null,
  searchText: '',
  typeFilter: 'all',
  status: '',
  dateFrom: '',
  dateTo: '',
  results: [],
  page: 0,
  hasMore: false,
  scrollTop: 0,
  lastQueryKey: '',
  keepState: false
};

interface PageResultPayload {
  page: number;
  hasMore: boolean;
  results: GlobalSearchResultItem[];
  replace: boolean;
  queryKey: string;
}

const globalSearchSlice = createSlice({
  name: 'globalSearch',
  initialState,
  reducers: {
    resetForTeam: (state, { payload }: PayloadAction<{ teamId: number; dateFrom: string }>) => ({
      ...initialState,
      teamId: payload.teamId,
      dateFrom: payload.dateFrom
    }),
    setSearchText: (state, { payload }: PayloadAction<string>) => {
      state.searchText = payload;
    },
    setTypeFilter: (state, { payload }: PayloadAction<GlobalSearchTypeFilter>) => {
      state.typeFilter = payload;
    },
    setStatus: (state, { payload }: PayloadAction<string>) => {
      state.status = payload;
    },
    setDateFrom: (state, { payload }: PayloadAction<string>) => {
      state.dateFrom = payload;
    },
    setDateTo: (state, { payload }: PayloadAction<string>) => {
      state.dateTo = payload;
    },
    setScrollTop: (state, { payload }: PayloadAction<number>) => {
      state.scrollTop = payload;
    },
    setPageResult: (state, { payload }: PayloadAction<PageResultPayload>) => {
      state.page = payload.page;
      state.hasMore = payload.hasMore;
      state.results = payload.replace ? payload.results : [...state.results, ...payload.results];
      state.lastQueryKey = payload.queryKey;
    },
    clearResults: state => {
      state.results = [];
      state.page = 0;
      state.hasMore = false;
      state.scrollTop = 0;
      state.lastQueryKey = '';
    },
    markKeepState: state => {
      state.keepState = true;
    },
    clearKeepState: state => {
      state.keepState = false;
    }
  },
  extraReducers: builder => {
    builder.addCase(loggedOut, () => initialState);
  }
});

export const selectGlobalSearch = (state: RootState) => state.rootReducer.globalSearch;

export const {
  resetForTeam,
  setSearchText,
  setTypeFilter,
  setStatus,
  setDateFrom,
  setDateTo,
  setScrollTop,
  setPageResult,
  clearResults,
  markKeepState,
  clearKeepState
} = globalSearchSlice.actions;

export default globalSearchSlice.reducer;
